import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BookMarked, Download, GitBranch, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "@/lib/api";
import RepoDetail from "./RepoDetail";
import RepoImporteren from "./RepoImporteren";
import { useTaal } from "@/contexts/TaalContext";
import TeamRolLabel from "./TeamRolLabel";
import { bewaarKeuze, filterKeuzes, filterRepos, geldigeKeuze, leesKeuze } from "@/lib/repo-filter";
import { repoOmschrijving } from "@/lib/repo-omschrijving";

/**
 * EG1 (opdracht Frank 31 oktober 2026): het eigen Git-systeem van NovaMund, het scherm Repo's.
 *
 * Een venster en geen route (App.js is bevroren). Indeling naar GitHub, zodat Frank zich meteen thuis voelt: de lijst
 * "Your repositories" met naam, omschrijving en takken, en "New" met de velden van "Create a new repository" (naam,
 * omschrijving; de README komt er vanzelf in, zoals Gitea `services/repository/init.go` met auto init). Alles via
 * `/api/repos`, dus ook vanaf de telefoon.
 */
export default function ReposVenster({ open, onOpenChange, onKies = null, onLosIssueOp = null }) {
  const { t, taal } = useTaal();
  const [repos, setRepos] = useState([]);
  const [stand, setStand] = useState(null);
  const [laden, setLaden] = useState(true);
  const [maken, setMaken] = useState(false);
  // EG9: importeren uit een map of van GitHub, naast Nieuwe repo.
  const [importeren, setImporteren] = useState(false);
  const [naam, setNaam] = useState("");
  const [omschrijving, setOmschrijving] = useState("");
  const [bezig, setBezig] = useState(false);
  // EG2: een gekozen repo opent in dit venster (bestanden en geschiedenis), zoals een repo op GitHub.
  const [gekozen, setGekozen] = useState(null);
  // Glasronde 0.9.235: alle repo's, met een filter bovenaan (Alle, Persoonlijk, per team) dat blijft staan.
  const [teams, setTeams] = useState([]);
  const [keuze, setKeuze] = useState(leesKeuze);

  const lees = useCallback(() => {
    setLaden(true);
    Promise.all([axios.get(`${API}/repos/status`), axios.get(`${API}/repos`)])
      .then(([s, r]) => {
        setStand(s.data);
        setRepos(r.data);
      })
      .then(() => axios.get(`${API}/teams`))
      .then((tm) => setTeams(Array.isArray(tm?.data) ? tm.data : []))
      .catch(() => {})
      .finally(() => setLaden(false));
  }, []);

  useEffect(() => {
    if (open) lees();
  }, [open, lees]);

  async function maak(e) {
    e?.preventDefault?.();
    setBezig(true);
    try {
      await axios.post(`${API}/repos`, { name: naam.trim(), description: omschrijving.trim() });
      setNaam("");
      setOmschrijving("");
      setMaken(false);
      lees();
    } catch (error) {
      toast.error(t(error.response?.data?.detail || "De repo kon niet worden gemaakt."));
    } finally {
      setBezig(false);
    }
  }

  const geenGit = stand && !stand.git;
  // Glasronde 0.9.235: een bewaard team waar je niet meer in zit wordt Alle; het aantal volgt het filter.
  const geldig = geldigeKeuze(keuze, teams);
  const zichtbaar = filterRepos(repos, geldig);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* WB4: dekkend in de lichte stand, sinds WB4b via DialogContent zelf (ui/dialog.jsx). */}
      <DialogContent className="max-w-3xl h-[85dvh] flex flex-col" data-testid="repos-venster">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookMarked className="h-4 w-4" />{t("Repo's")}</DialogTitle>
          <DialogDescription>
            {t("Je eigen Git-systeem: elke repo staat op deze Mac, in de map van NovaMund. De hoofdtak heet main.")}
          </DialogDescription>
        </DialogHeader>

        {geenGit && (
          <p className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-sm" data-testid="git-ontbreekt">
            {t(stand.melding)}
          </p>
        )}

        {gekozen ? (
          <RepoDetail repo={gekozen} onTerug={() => setGekozen(null)} onOpenInCode={onKies}
                      onLosIssueOp={onLosIssueOp ? (n) => { onOpenChange(false); onLosIssueOp(gekozen, n); } : null} />
        ) : (
        <>
        <div className="flex flex-wrap items-center gap-2">
          <select value={geldig} onChange={(e) => { setKeuze(e.target.value); bewaarKeuze(e.target.value); }}
                  aria-label={t("Welke repo's")} className="rounded-md border bg-transparent px-1 py-1 text-sm"
                  data-testid="repos-filter">
            {filterKeuzes(teams).map((k) => (
              <option key={k.id} value={k.id}>{k.team ? t("Team {team}", { team: k.naam }) : t(k.naam)}</option>
            ))}
          </select>
          <span className="text-sm text-muted-foreground" data-testid="repos-telling">
            {zichtbaar.length === 1 ? t("1 repo") : t("{n} repo's", { n: zichtbaar.length })}
          </span>
          <Button size="sm" variant="outline" className="tap-target ml-auto" disabled={geenGit} data-testid="repo-importeer-knop"
                  onClick={() => { setImporteren((m) => !m); setMaken(false); }}>
            <Download className="h-4 w-4 mr-1" />{t("Importeren")}
          </Button>
          <Button size="sm" className="tap-target" onClick={() => { setMaken((m) => !m); setImporteren(false); }} disabled={geenGit}
                  data-testid="nieuwe-repo">
            <Plus className="h-4 w-4 mr-1" />{t("Nieuwe repo")}
          </Button>
        </div>

        {importeren && <RepoImporteren onKlaar={() => { setImporteren(false); lees(); }} />}

        {maken && (
          <form onSubmit={maak} className="space-y-2 rounded-lg border p-3" data-testid="repo-formulier">
            <label className="block text-sm font-medium" htmlFor="repo-naam">{t("Naam van de repo")}</label>
            <Input id="repo-naam" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="proef"
                   data-testid="repo-naam" autoFocus />
            <label className="block text-sm font-medium" htmlFor="repo-omschrijving">{t("Omschrijving (mag leeg)")}</label>
            <Input id="repo-omschrijving" value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)}
                   data-testid="repo-omschrijving" />
            <p className="text-xs text-muted-foreground">{t("De repo krijgt meteen een README op main.")}</p>
            <Button type="submit" className="tap-target" disabled={bezig || !naam.trim()} data-testid="repo-aanmaken">
              {bezig && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{t("Repo aanmaken")}
            </Button>
          </form>
        )}

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
          {laden && <p className="text-sm text-muted-foreground">{t("Laden…")}</p>}
          {!laden && repos.length > 0 && zichtbaar.length === 0 && (
            <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground" data-testid="geen-repos-in-filter">
              {t("Geen repo's in dit filter. Kies Alle om alles te zien.")}
            </p>
          )}
          {!laden && repos.length === 0 && !geenGit && (
            <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="geen-repos">
              {t("Nog geen repo's. Maak er een met Nieuwe repo.")}
            </p>
          )}
          {zichtbaar.map((r) => (
            <button key={r.id} type="button" onClick={() => setGekozen(r)}
                    className="tap-target flex w-full items-start gap-3 rounded-lg border p-3 text-left hover:bg-muted/50"
                    data-testid={`repo-${r.name}`}>
              <BookMarked className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-primary">
                  {r.name}
                  {r.bron?.soort === "github" && (
                    <span className="ml-2 rounded-full border px-2 text-xs font-normal text-muted-foreground" data-testid="repo-is-spiegel">
                      {t("spiegel van GitHub")}
                    </span>
                  )}
                  {r.team_naam && <span className="ml-2"><TeamRolLabel teamNaam={r.team_naam} rol={r.rol} testid="repo-lijst-team" /></span>}
                </span>
                {r.description && <span className="block truncate text-xs text-muted-foreground">{repoOmschrijving(r.description, taal)}</span>}
              </span>
              <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground" data-testid="repo-takken">
                <GitBranch className="h-3 w-3" />
                {(r.branches || []).length === 1 ? t("1 tak") : t("{n} takken", { n: (r.branches || []).length })}
              </span>
            </button>
          ))}
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
